import axios from "axios";
import { useEffect, useRef, useState } from "react";
import CountUp from "react-countup";
import { FaBuilding, FaSearch, FaUserTag } from "react-icons/fa";
import { FiCalendar, FiCheckCircle, FiClock, FiList, FiXCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { isEmployeeHidden } from "../utils/employeeStatus";

const Regularization = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Employees data for department/designation
  const [employees, setEmployees] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // Default to current month
  const [selectedDate, setSelectedDate] = useState(""); // Specific date filter
  
  // Department and Designation filter states
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterDesignation, setFilterDesignation] = useState("");
  const [showDepartmentFilter, setShowDepartmentFilter] = useState(false);
  const [showDesignationFilter, setShowDesignationFilter] = useState(false);

  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);

  // Unique departments and designations
  const [uniqueDepartments, setUniqueDepartments] = useState([]);
  const [uniqueDesignations, setUniqueDesignations] = useState([]);

  // Refs for click outside
  const departmentFilterRef = useRef(null);
  const designationFilterRef = useRef(null);

  // Click outside handlers for filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (departmentFilterRef.current && !departmentFilterRef.current.contains(event.target)) {
        setShowDepartmentFilter(false);
      }
      if (designationFilterRef.current && !designationFilterRef.current.contains(event.target)) {
        setShowDesignationFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Group records by Employee
  const groupRecordsByEmployee = (records) => {
    const grouped = new Map();
    
    records.forEach(rec => {
      if (!grouped.has(rec.employeeId)) {
        grouped.set(rec.employeeId, {
          employeeId: rec.employeeId,
          records: []
        });
      }
      grouped.get(rec.employeeId).records.push(rec);
    });
    
    return Array.from(grouped.values());
  };

  // ✅ Fetch all edited records and employees
  const fetchEditedRecords = async () => {
    try {
      setLoading(true);
      const recordsUrl = selectedDate 
        ? `${API_BASE_URL}/attendancesummary/edited-records?date=${selectedDate}`
        : `${API_BASE_URL}/attendancesummary/edited-records?month=${selectedMonth}`;

      const [recordsRes, empRes] = await Promise.all([
        axios.get(recordsUrl),
        axios.get(`${API_BASE_URL}/employees/get-employees`)
      ]);

      const employeesData = empRes.data || [];
      const activeEmployees = employeesData.filter(emp => !isEmployeeHidden(emp));
      setEmployees(activeEmployees);

      // Extract unique departments and designations
      const depts = new Set();
      const designations = new Set();
      activeEmployees.forEach(emp => {
        if (emp.department) depts.add(emp.department);
        if (emp.role || emp.designation) designations.add(emp.role || emp.designation);
      });
      setUniqueDepartments(Array.from(depts).sort());
      setUniqueDesignations(Array.from(designations).sort());

      const editedRecords = recordsRes.data.data || [];
      
      // Filter out records from hidden employees
      const activeEmployeeIds = new Set(activeEmployees.map(emp => emp.employeeId));
      const filteredRecords = editedRecords.filter(rec => activeEmployeeIds.has(rec.employeeId));

      const grouped = groupRecordsByEmployee(filteredRecords);
      
      setRequests(grouped);
      setFilteredRequests(grouped);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to fetch edited records:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEditedRecords();
  }, [selectedMonth, selectedDate]);

  // Get employee department and designation
  const getEmployeeDetails = (employeeId) => {
    const emp = employees.find(e => e.employeeId === employeeId);
    return {
      name: emp?.name || "N/A",
      department: emp?.department || emp?.departmentName || "N/A",
      designation: emp?.designation || emp?.role || "N/A",
      profilePicture: emp?.profilePicture
    };
  };

  // ✅ Apply Filters
  useEffect(() => {
    let filtered = [...requests];

    if (searchTerm) {
      filtered = filtered.filter((group) => {
        const empDetails = getEmployeeDetails(group.employeeId);
        return (
          empDetails.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          group.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    // Filter by Department
    if (filterDepartment) {
      filtered = filtered.filter(group => {
        const empDetails = getEmployeeDetails(group.employeeId);
        return empDetails.department === filterDepartment;
      });
    }

    // Filter by Designation
    if (filterDesignation) {
      filtered = filtered.filter(group => {
        const empDetails = getEmployeeDetails(group.employeeId);
        return empDetails.designation === filterDesignation;
      });
    }

    setFilteredRequests(filtered);
    setCurrentPage(1);
  }, [
    searchTerm,
    filterDepartment,
    filterDesignation,
    requests,
  ]);

  // ✅ Clear Filters
  const clearFilters = () => {
    setSearchTerm("");
    setFilterDepartment("");
    setFilterDesignation("");
    setSelectedMonth(new Date().toISOString().slice(0, 7));
  };

  const navigate = useNavigate();

  // ✅ Pagination Handlers
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 2 && i <= currentPage + 2)
      ) {
        pageNumbers.push(i);
      } else if (i === currentPage - 3 || i === currentPage + 3) {
        pageNumbers.push("...");
      }
    }
    return pageNumbers;
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  // ✅ Stat Box - Matching the Dashboard design
  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className={`bg-[#0a0a0a] rounded-lg p-3 shadow-sm border-t-4 ${color} cursor-pointer hover:shadow-md transition-all duration-300 flex items-center justify-between`}>
      <div className="flex items-center gap-2">
        <Icon className="text-gray-400 text-base flex-shrink-0" />
        <div className="text-sm font-medium text-gray-300">{label}</div>
      </div>
      <div className="text-sm font-bold text-gray-300">
        <CountUp end={value} duration={2} separator="," />
      </div>
    </div>
  );

  // ✅ Formatting functions
  const formatTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // ✅ View Details Handler
  const handleViewDetails = (group) => {
    const empDetails = getEmployeeDetails(group.employeeId);
    setSelectedEmployeeData({
      ...empDetails,
      employeeId: group.employeeId,
      records: group.records.sort((a, b) => new Date(b.checkInTime) - new Date(a.checkInTime))
    });
    setShowDetailsModal(true);
  };

  // ✅ Loading Screen
  if (loading && requests.length === 0)
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 to-blue-100">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 border-b-2 border-purple-600 rounded-full animate-spin"></div>
          <p className="font-semibold text-gray-400">
            Loading edited records...
          </p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen px-2 py-2 bg-gradient-to-br from-purple-50 to-blue-100">
      <div className="mx-auto max-w-9xl">
        {/* ✅ Stats */}
        <div className="grid grid-cols-2 gap-2 mb-2 sm:grid-cols-4">
          <StatCard
            icon={FiList}
            label="Employees Edited"
            value={requests.length}
            color="border-purple-500"
          />
          <StatCard
            icon={FiCheckCircle}
            label="Total Edits"
            value={requests.reduce((acc, curr) => acc + curr.records.length, 0)}
            color="border-green-500"
          />
          <StatCard
            icon={FiCalendar}
            label="Selected Month"
            value={requests.length > 0 ? 1 : 0}
            color="border-blue-500"
          />
          <StatCard
            icon={FiClock}
            label="Filter Active"
            value={searchTerm || filterDepartment || filterDesignation ? 1 : 0}
            color="border-yellow-500"
          />
        </div>


        {/* Filters Section */}
        <div className="p-3 mb-3 bg-[#0a0a0a] rounded-lg shadow-md">
          <div className="flex flex-wrap items-center gap-2">

            {/* Month Filter */}
            <div className="relative w-[150px]">
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(e.target.value);
                    setSelectedDate(""); // Clear date if month is selected
                  }}
                  className="w-full px-3 py-1.5 text-xs border border-[#374151] rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Date Filter */}
              <div className="relative w-[150px]">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    // month remains for context but priority is date
                  }}
                  className="w-full px-3 py-1.5 text-xs border border-[#374151] rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

            {/* ID/Name Search */}
            <div className="relative flex-1 min-w-[180px]">
              <FaSearch className="absolute text-sm text-gray-400 transform -translate-y-1/2 left-2 top-1/2" />
              <input
                type="text"
                placeholder="Search by ID or Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-[#374151] rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Department Filter Button */}
            <div className="relative" ref={departmentFilterRef}>
              <button
                onClick={() => setShowDepartmentFilter(!showDepartmentFilter)}
                className={`h-8 px-3 text-xs font-medium rounded-md transition flex items-center gap-1 ${filterDepartment
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-[#111111] text-gray-300 hover:bg-[#1f2937] border border-[#374151]'
                  }`}
              >
                <FaBuilding className="text-xs" /> Dept {filterDepartment && `: ${filterDepartment}`}
              </button>

              {/* Department Filter Dropdown */}
              {showDepartmentFilter && (
                <div className="absolute z-50 w-48 mt-1 overflow-y-auto bg-[#0a0a0a] border border-[#1f2937] rounded-md shadow-lg max-h-60">
                  <div
                    onClick={() => {
                      setFilterDepartment('');
                      setShowDepartmentFilter(false);
                    }}
                    className="px-3 py-2 text-xs font-medium text-gray-300 border-b border-[#1f2937] cursor-pointer hover:bg-blue-50"
                  >
                    All Departments
                  </div>
                  {uniqueDepartments.map(dept => (
                    <div
                      key={dept}
                      onClick={() => {
                        setFilterDepartment(dept);
                        setShowDepartmentFilter(false);
                      }}
                      className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${filterDepartment === dept ? 'bg-blue-50 text-blue-700 font-medium' : ''
                        }`}
                    >
                      {dept}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Designation Filter Button */}
            <div className="relative" ref={designationFilterRef}>
              <button
                onClick={() => setShowDesignationFilter(!showDesignationFilter)}
                className={`h-8 px-3 text-xs font-medium rounded-md transition flex items-center gap-1 ${filterDesignation
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-[#111111] text-gray-300 hover:bg-[#1f2937] border border-[#374151]'
                  }`}
              >
                <FaUserTag className="text-xs" /> Desig {filterDesignation && `: ${filterDesignation}`}
              </button>

              {/* Designation Filter Dropdown */}
              {showDesignationFilter && (
                <div className="absolute z-50 w-48 mt-1 overflow-y-auto bg-[#0a0a0a] border border-[#1f2937] rounded-md shadow-lg max-h-60">
                  <div
                    onClick={() => {
                      setFilterDesignation('');
                      setShowDesignationFilter(false);
                    }}
                    className="px-3 py-2 text-xs font-medium text-gray-300 border-b border-[#1f2937] cursor-pointer hover:bg-blue-50"
                  >
                    All Designations
                  </div>
                  {uniqueDesignations.map(des => (
                    <div
                      key={des}
                      onClick={() => {
                        setFilterDesignation(des);
                        setShowDesignationFilter(false);
                      }}
                      className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${filterDesignation === des ? 'bg-blue-50 text-blue-700 font-medium' : ''
                        }`}
                    >
                      {des}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Clear Filters Button */}
            {(searchTerm || filterDepartment || filterDesignation || selectedMonth !== new Date().toISOString().slice(0, 7)) && (
              <button
                onClick={clearFilters}
                className="h-8 px-3 text-xs font-medium text-gray-400 transition bg-[#111111] border border-[#374151] rounded-md hover:bg-[#1f2937]"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* ✅ Main Employee Table */}
        <div className="mb-6 overflow-hidden bg-[#0a0a0a] rounded-lg shadow-lg">
          <div className="overflow-x-auto bg-[#0a0a0a] shadow-lg rounded-xl">
            <table className="min-w-full">
              <thead className="text-sm text-left text-white bg-gradient-to-r from-green-500 to-blue-600">
                <tr>
                  <th className="py-2 text-center ">Employee ID</th>
                  <th className="py-2 text-center ">Name</th>
                  <th className="py-2 text-center ">Department</th>
                  <th className="py-2 text-center ">Designation</th>
                  <th className="py-2 text-center ">Edit Count</th>
                  <th className="py-2 text-center rounded-tr-lg ">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((group) => {
                    const empDetails = getEmployeeDetails(group.employeeId);
                    return (
                      <tr
                        key={group.employeeId}
                        className="transition border-b hover:bg-[#000000]"
                      >
                        <td className="px-2 py-2 font-medium text-center text-gray-200 whitespace-nowrap">
                          {group.employeeId || "N/A"}
                        </td>
                        <td className="px-2 py-2 font-medium text-center text-gray-200 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-2">
                             {empDetails.profilePicture ? (
                               <img src={empDetails.profilePicture} className="w-6 h-6 rounded-full" />
                             ) : (
                               <div className="flex items-center justify-center w-6 h-6 text-[10px] bg-purple-100 text-purple-600 rounded-full">
                                  {empDetails.name.charAt(0)}
                               </div>
                             )}
                             <div className="font-medium text-xs">{empDetails.name}</div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center text-xs text-gray-400 ">
                          {empDetails.department}
                        </td>
                        <td className="px-2 py-2 text-center text-xs text-gray-400 ">
                          {empDetails.designation}
                        </td>
                        <td className="px-2 py-2 text-center">
                           <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-bold">
                                {group.records.length} edits
                           </span>
                        </td>
                        <td className="px-2 py-2 font-medium text-center text-gray-200 whitespace-nowrap">
                           <button 
                             onClick={() => handleViewDetails(group)}
                             className="px-3 py-1 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                           >
                             View Details
                           </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="py-6 text-center text-gray-500">
                      {loading ? "Loading..." : "No edited records found for this month."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ✅ Modal for Date-wise Details */}
        {showDetailsModal && selectedEmployeeData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="w-full max-w-4xl max-h-[90vh] bg-[#0a0a0a] rounded-xl shadow-2xl overflow-hidden animate-fade-in flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 text-white bg-gradient-to-r from-blue-600 to-indigo-700">
                <div className="flex items-center gap-3">
                   <div className="flex items-center justify-center w-10 h-10 bg-[#0a0a0a] bg-opacity-20 rounded-full">
                       <FaUserTag className="text-xl" />
                   </div>
                   <div>
                     <h3 className="text-lg font-bold leading-tight">{selectedEmployeeData.name} ({selectedEmployeeData.employeeId})</h3>
                     <p className="text-xs text-blue-100">{selectedEmployeeData.department} • {selectedEmployeeData.designation}</p>
                   </div>
                </div>
                <button 
                  onClick={() => setShowDetailsModal(false)}
                  className="p-1 transition-colors rounded-full hover:bg-[#0a0a0a] hover:bg-opacity-10"
                >
                  <FiXCircle className="text-2xl" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 p-6 overflow-y-auto bg-[#000000]">
                <div className="overflow-hidden bg-[#0a0a0a] border border-[#1f2937] rounded-lg shadow-sm">
                  <table className="min-w-full">
                    <thead className="bg-[#111111] border-b border-[#1f2937]">
                      <tr>
                        <th className="px-4 py-3 text-xs font-semibold text-center text-gray-300 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-xs font-semibold text-center text-gray-300 uppercase tracking-wider">Times (In / Out)</th>
                        <th className="px-4 py-3 text-xs font-semibold text-center text-gray-300 uppercase tracking-wider">Hours</th>
                        <th className="px-4 py-3 text-xs font-semibold text-center text-gray-300 uppercase tracking-wider">Admin Comment</th>
                        <th className="px-4 py-3 text-xs font-semibold text-center text-gray-300 uppercase tracking-wider">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1f2937]">
                      {selectedEmployeeData.records.map((rec) => (
                        <tr key={rec._id} className="hover:bg-blue-50 transition-colors">
                          <td className="px-4 py-4 text-xs font-medium text-center text-gray-200">
                            {new Date(rec.checkInTime).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="inline-flex flex-col gap-1 px-3 py-1 bg-[#000000] rounded-lg border border-[#1f2937] shadow-inner">
                              <span className="text-[11px] font-bold text-green-600 flex items-center justify-center gap-1">
                                <FiClock className="inline" /> {formatTime(rec.checkInTime)}
                              </span>
                              <span className="text-[11px] font-bold text-indigo-600 flex items-center justify-center gap-1">
                                <FiClock className="inline" /> {formatTime(rec.checkOutTime)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="px-2 py-1 text-xs font-bold text-blue-700 bg-blue-50 rounded-md border border-blue-100">
                              {rec.totalHours?.toFixed(2) || "0.00"}h
                            </span>
                          </td>
                          <td className="px-4 py-4 text-xs text-center text-gray-400 italic">
                            {rec.comment || <span className="text-gray-300">No comment</span>}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="px-3 py-1 text-[10px] font-bold text-white uppercase tracking-wider rounded-full bg-indigo-500 shadow-sm">
                              {rec.reason || "Edited"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-[#000000] border-t border-[#1f2937] flex justify-end">
                <button 
                  onClick={() => setShowDetailsModal(false)}
                  className="px-6 py-2 text-sm font-semibold text-gray-300 bg-[#0a0a0a] border border-[#374151] rounded-lg shadow-sm hover:bg-[#000000] transition-all focus:ring-2 focus:ring-blue-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Pagination Section */}
        {filteredRequests.length > 0 && (
          <div className="flex flex-col items-center justify-between gap-4 mt-6 sm:flex-row">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="font-medium text-gray-300 ">
                  Show:
                </label>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="p-2 border rounded-lg"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-400">entries</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`px-4 py-1 text-sm border rounded-lg ${currentPage === 1
                    ? "text-gray-400 bg-[#111111] cursor-not-allowed"
                    : "text-blue-600 bg-[#0a0a0a] hover:bg-blue-50 border-blue-200"
                  }`}
              >
                Previous
              </button>

              {getPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === 'number' ? handlePageClick(page) : null}
                  disabled={page === "..."}
                  className={`px-4 py-1 text-sm border rounded-lg ${page === "..."
                      ? "text-gray-500 bg-[#000000] cursor-default"
                      : currentPage === page
                        ? "text-white bg-blue-600 border-blue-600"
                        : "text-blue-600 bg-[#0a0a0a] hover:bg-blue-50 border-blue-300"
                    }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`px-4 py-1 text-sm border rounded-lg ${currentPage === totalPages
                    ? "text-gray-400 bg-[#111111] cursor-not-allowed"
                    : "text-blue-600 bg-[#0a0a0a] hover:bg-blue-50 border-blue-300"
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Regularization;